import {
  type AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  type ElementRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { assertIsNotNil } from '@pct-ts/assert';
import type { TableCellComponent } from '../table-cell/table-cell.component';

@Component({
  selector: 'pct-feature-table-material-text-ellipsis-cell-component',
  template: `
    <div
      #textElement
      class="whitespace-nowrap overflow-hidden text-ellipsis max-w-[100%]"
      [matTooltip]="isEllipsisActive() ? value() : null"
    >
      {{ value() }}
    </div>
  `,
  imports: [MatTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // TODO width to input
    class: 'block min-w-[150px] max-w-[300px]',
  },
})
export class TextEllipsisCellComponent<T>
  implements TableCellComponent<T, string, any>, AfterViewInit
{
  readonly rowItem = input.required<T>();
  readonly value = input.required<string>();
  readonly params = input.required<any>();

  readonly scrollWidth = signal(0);
  readonly clientWidth = signal(0);
  readonly isEllipsisActive = signal(false);

  private readonly textElement = viewChild<ElementRef<HTMLDivElement>>('textElement');

  constructor() {
    effect(() => {
      setTimeout(() => this.#checkEllipsis(), 0);
    });
  }

  ngAfterViewInit(): void {
    this.#checkEllipsis();

    const textElement = this.textElement();
    if (textElement !== undefined) {
      const resizeObserver = new ResizeObserver(() => {
        this.#checkEllipsis();
      });
      const nativeElement = textElement?.nativeElement;
      assertIsNotNil(
        nativeElement,
        'Text element native element must be available for ResizeObserver',
      );
      resizeObserver.observe(nativeElement);
    }
  }

  #checkEllipsis(): void {
    const textElement = this.textElement();
    if (textElement === undefined || textElement.nativeElement === undefined) {
      return;
    }

    const { scrollWidth, clientWidth } = textElement.nativeElement;

    this.scrollWidth.set(scrollWidth);
    this.clientWidth.set(clientWidth);
    this.isEllipsisActive.set(scrollWidth > clientWidth);
  }
}
